/* CallControls — mic mute, speaker mute, volume slider for an active
 * agent-desk call.
 *
 * Splits responsibility:
 *   - Mic side goes through the SDK (`setMicMuted` from useTelnyxClient).
 *     That disables the outbound RTP track so Telnyx — and therefore
 *     the customer — hears silence rather than just a quiet local mic.
 *   - Speaker side talks directly to the page's hidden #agent-desk-
 *     remote-audio element (mute toggles `audio.muted`, slider drives
 *     `audio.volume`). No SDK call needed; the inbound stream is just
 *     a MediaStream attached to that element.
 *
 * Volume preference persists across calls in localStorage so an agent
 * who likes 60% doesn't have to rediscover it every pickup. Mic and
 * speaker mute states reset per call (always start unmuted) so the
 * agent can't accidentally answer deaf or mute. */

import { useCallback, useEffect, useState } from "react";

import { MicMutedIcon, MicOnIcon, SpeakerMutedIcon, SpeakerOnIcon } from "../components/icons";

const REMOTE_AUDIO_ID = "agent-desk-remote-audio";
const VOLUME_STORAGE_KEY = "agent-desk:speaker-volume";
const DEFAULT_VOLUME = 1;

function readSavedVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (raw === null) return DEFAULT_VOLUME;
    const v = Number(raw);
    if (!Number.isFinite(v) || v < 0 || v > 1) return DEFAULT_VOLUME;
    return v;
  } catch {
    return DEFAULT_VOLUME;
  }
}

function getAudioElement(): HTMLAudioElement | null {
  return document.getElementById(REMOTE_AUDIO_ID) as HTMLAudioElement | null;
}

export function CallControls({
  micMuted,
  onMicToggle,
}: {
  micMuted: boolean;
  onMicToggle: (next: boolean) => void;
}) {
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [volume, setVolume] = useState<number>(readSavedVolume);

  // Apply volume to the audio element. Effect runs on every change AND
  // after the audio element first appears in the DOM (it's rendered by
  // AgentDesk, so by the time CallControls mounts inside Softphone the
  // element already exists — but defensive lookup keeps us safe).
  useEffect(() => {
    const el = getAudioElement();
    if (!el) return;
    el.volume = volume;
  }, [volume]);

  // Apply speaker mute. Independent of volume so muting → unmuting
  // restores the previous level rather than jumping to 0%.
  useEffect(() => {
    const el = getAudioElement();
    if (!el) return;
    el.muted = speakerMuted;
  }, [speakerMuted]);

  // Persist volume across reloads. Skip the very first effect run so we
  // don't overwrite the saved value with itself before the user has
  // touched the slider.
  const persistVolume = useCallback((v: number) => {
    try {
      localStorage.setItem(VOLUME_STORAGE_KEY, String(v));
    } catch {
      /* private mode */
    }
  }, []);

  return (
    <div className="agent-desk-call-controls" aria-label="Call controls">
      <button
        type="button"
        className={`agent-desk-call-btn ${micMuted ? "is-muted" : ""}`}
        onClick={() => onMicToggle(!micMuted)}
        title={micMuted ? "Unmute your mic" : "Mute your mic"}
        aria-pressed={micMuted}
      >
        {micMuted ? <MicMutedIcon size={14} /> : <MicOnIcon size={14} />}
        <span className="agent-desk-call-btn-label">
          {micMuted ? "Unmute" : "Mute"}
        </span>
      </button>

      <button
        type="button"
        className={`agent-desk-call-btn ${speakerMuted ? "is-muted" : ""}`}
        onClick={() => setSpeakerMuted((m) => !m)}
        title={speakerMuted ? "Unmute the caller" : "Mute the caller"}
        aria-pressed={speakerMuted}
      >
        {speakerMuted ? <SpeakerMutedIcon size={14} /> : <SpeakerOnIcon size={14} />}
      </button>

      <label
        className="agent-desk-call-volume"
        title={`Volume ${Math.round(volume * 100)}%`}
      >
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={volume}
          onChange={(e) => {
            const v = Number(e.target.value);
            setVolume(v);
            persistVolume(v);
          }}
          aria-label="Caller volume"
        />
      </label>
    </div>
  );
}
